import { Injectable, Output, EventEmitter } from '@angular/core';
import { AccountType, Preferences, User } from '../models/user';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Observable, of, ReplaySubject, Subject } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { StateStorageService } from '../core/auth/state-storage.service';
import { Router } from '@angular/router';
import { compare } from 'fast-json-patch';
import { Password, PasswordApi } from '../core/password/password.model';
import { MenuService } from './menu.service';
import { SmsService } from './sms.service';
import { setNumeralLocaleByNation } from '../utils/utils';
import { SessionStorageService } from 'ngx-webstorage';
import * as psl from 'psl';
import { ApiMDelegatedService } from '@services/api-m-delegated.service';
import moment from 'moment';
import { LoaderService } from './loader.service';
import { Subscription } from '@models/credits';
import { TokenExchangeResponse } from '../core/login/login.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly baseUrl;
  private userIdentity: User | null = null;
  private authenticationState = new ReplaySubject<User | null>(1);
  private accountCache$?: Observable<User | null>;

  @Output() updateBillingDetails: EventEmitter<any> = new EventEmitter();

  private headearSubscription$ = new Subject<Subscription[]>();

  constructor(
    private http: HttpClient,
    private stateStorageService: StateStorageService,
    private router: Router,
    private menuService: MenuService,
    private sessionStorage: SessionStorageService,
    private smsService: SmsService,
    private apiMDelegatedService: ApiMDelegatedService,
    private loaderService: LoaderService
  ) {
    this.baseUrl = environment.serverBaseUrl;
  }

  public fetch(): Observable<User> {
    return this.http.get<Response>(this.baseUrl + 'user').pipe(map(data => User.deserialize(data)));
  }

  public updateUserPreferences(preferences: any, formData: any): Observable<any> {
    const preferencesPatch = compare(preferences, formData);
    const patchHeaders = new HttpHeaders().set('Content-type', 'application/json-patch+json');
    const url = 'user/preferences';
    return this.http
      .patch<Response>(this.baseUrl + url, preferencesPatch, { headers: patchHeaders })
      .pipe(map(data => User.deserialize(data)));
  }

  public updateUserBilling(user: User, formData: any): Observable<any> {
    this.checkTaxCodeAndProvince(user, formData);
    const billingPatch = compare(user.billing, formData);
    const patchHeaders = new HttpHeaders().set('Content-type', 'application/json-patch+json');
    const url = 'user/billing';
    return this.http
      .patch<Response>(this.baseUrl + url, billingPatch, { headers: patchHeaders })
      .pipe(map(data => User.deserialize(data)));
  }
  // The taxcode and province fields show on FE if only if nation is ITA,
  // If they are empty then you need to initialize otherwise you get a validation error from BE
  private checkTaxCodeAndProvince(user: User, formData: any) {
    if (user.nation !== 'ita') {
      formData.taxCode = formData.vatCode ? formData.vatCode : '';
      formData.province = formData.city ? formData.city : '';
    }
  }

  public updateUserProfile(user: User, formData: User): Observable<User> {
    const userPatch = compare(user, formData);
    const patchHeaders = new HttpHeaders().set('Content-type', 'application/json-patch+json');
    return this.http
      .patch<Response>(this.baseUrl + 'user', userPatch, { headers: patchHeaders })
      .pipe(map(data => User.deserialize(data)));
  }

  updatePassword(password: Password): Observable<string> {
    return this.http.post(this.baseUrl + 'password', password, { responseType: 'text' });
  }

  updatePasswordAPI(password: PasswordApi): Observable<string> {
    return this.http.post(this.baseUrl + 'password/api', password, { responseType: 'text' });
  }

  getAuthenticationState(): Observable<User | null> {
    return this.authenticationState.asObservable();
  }

  public isLoggedIn(): boolean {
    return this.userIdentity !== null;
  }

  public isSubaccount(): boolean {
    return (
      this.userIdentity.account !== null &&
      'subaccount' in this.userIdentity.account &&
      this.userIdentity.account &&
      this.userIdentity !== null
    );
  }

  authenticate(identity: User | null): void {
    this.userIdentity = identity;
    if (identity && identity.nation) {
      setNumeralLocaleByNation(identity.nation);
    }
    this.authenticationState.next(this.userIdentity);
  }

  isAuthenticated(): boolean {
    return this.userIdentity !== null;
  }

  private navigateToStoredUrl(): void {
    // previousState can be set in the authExpiredInterceptor and in the userRouteAccessService
    // if login is successful, go to stored previousState and clear previousState
    const previousUrl = this.stateStorageService.getUrl();
    if (previousUrl) {
      this.stateStorageService.clearUrl();
      this.router.navigateByUrl(previousUrl);
    }
  }

  identity(force?: boolean): Observable<any | null> {
    if (!this.accountCache$ || force || !this.isAuthenticated()) {
      this.accountCache$ = this.fetch().pipe(
        catchError(() => {
          return of(null);
        }),
        tap((account: User | null) => {
          this.authenticate(account);
          if (account) {
            this.navigateToStoredUrl();
          }
        }),
        tap((account: User | null) => {
          if (account) {
            this.sessionStorage.store('preferences', {
              currency: account.currency,
              dateFormat: account.preferences.dateFormat,
              hourFormat: account.preferences.hourFormat
            }); // Used by utils
            if (account?.profile?.role?.id) {
              this.sessionStorage.store('wsb-userprofile-role', account.profile.role.id);
            }
          }
        }),
        tap((account: User | null) => {
          if (account) {
            this.menuService.updateMenuEntries(force);
            this.smsService.getMessageTypes(force);
          }
        }, shareReplay())
      );
    }
    return this.accountCache$;
  }

  checkRedirectApiMDelegation(): boolean {
    const redirectAfterLogin = this.sessionStorage.retrieve('redirect-after-login-api-m-delegation');
    if (!redirectAfterLogin) {
      return false;
    }
    this.sessionStorage.clear('redirect-after-login-api-m-delegation');
    this.apiMDelegatedService.login(redirectAfterLogin.salt, redirectAfterLogin.returnUrl, redirectAfterLogin.sig).subscribe(
      ret => {
        let redirectUrl = ret['redirect-url'];
        const redirectUrlTmp = new URL(redirectUrl);
        redirectUrl = this.getApiMDomain() + redirectUrlTmp.pathname + redirectUrlTmp.search;
        window.location.href = redirectUrl;
      },
      error => {
        window.location.href = '/';
      }
    );
    return true;
  }

  getApiMDomain(): string {
    if (window.location.host.substr(0, 5) === 'test-') {
      return 'https://developers-dev.esendex.it';
    }
    return 'https://developers.esendex.it';
  }

  checkRedirectToReturnUrl(tokenData: TokenExchangeResponse): boolean {
    let redirectAfterLogin = this.sessionStorage.retrieve('redirect-after-login');
    const authcallbackAfterLogin = this.sessionStorage.retrieve('authcallback-after-login');
    const parsedCurDomain = psl.parse(window.location.hostname);
    if (redirectAfterLogin && authcallbackAfterLogin) {
      // fix to delete $ at the beginning of the string
      if (redirectAfterLogin.charAt(0) === '$') {
        redirectAfterLogin = redirectAfterLogin.substr(1);
      }
      const parsedRedirectDomain = psl.parse(new URL(redirectAfterLogin).hostname);
      const parsedCallbackDomain = psl.parse(new URL(authcallbackAfterLogin).hostname);
      if (
        (parsedCurDomain.domain === parsedRedirectDomain.domain && // security check
          parsedCurDomain.domain === parsedCallbackDomain.domain) || // security check
        ('esendex.it' === parsedRedirectDomain.domain && 'esendex.it' === parsedCallbackDomain.domain)
      ) {
        this.sessionStorage.clear('redirect-after-login');
        this.sessionStorage.clear('authcallback-after-login');
        const href =
          authcallbackAfterLogin +
          '?' +
          'user_id' +
          '=' +
          encodeURIComponent(tokenData.user_id) +
          '&' +
          'user_profile_id' +
          '=' +
          encodeURIComponent(tokenData.user_profile_id) +
          '&' +
          'access_token' +
          '=' +
          encodeURIComponent(tokenData.atlas_token) +
          '&' +
          'hostname' +
          '=' +
          encodeURIComponent(window.location.hostname) +
          '&' +
          'return_url' +
          '=' +
          encodeURIComponent(redirectAfterLogin);
        window.location.href = href;
        this.loaderService.hide();
        return true;
      }
    } else if (redirectAfterLogin) {
      this.sessionStorage.clear('redirect-after-login');
      window.location.href = redirectAfterLogin;
      this.loaderService.hide();
    }
    return false;
  }

  isImpersonatingSubuser() {
    return this.sessionStorage.retrieve('wsb-impersonate-subuser-key') !== null;
  }

  isImpersonatingIGo() {
    return this.sessionStorage.retrieve('wsb-igo-impersonator') !== null;
  }

  getSessionUserId() {
    return this.sessionStorage.retrieve('wsb-user-key');
  }

  setMessageObfuscationType(type: string): Observable<Preferences> {
    const patch = [
      {
        op: 'replace',
        path: '/messageObfuscationType',
        value: type
      }
    ];
    const patchHeaders = new HttpHeaders().set('Content-type', 'application/json-patch+json');
    return this.http
      .patch<Response>(this.baseUrl + 'user/preferences', patch, { headers: patchHeaders })
      .pipe(map(data => Preferences.deserialize(data)));
  }

  setPrivacyPopUpDate(): Observable<Preferences> {
    const timezone = moment()
      .format('Z')
      .replace(':', '');

    const patch = [
      {
        op: 'replace',
        path: '/hidePrivacyPopUpDate',
        value:
          moment()
            .format('YYYY-MM-DDTHH:mm:ss')
            .toString() + timezone
      }
    ];
    const patchHeaders = new HttpHeaders().set('Content-type', 'application/json-patch+json');
    return this.http
      .patch<Response>(this.baseUrl + 'user/preferences', patch, { headers: patchHeaders })
      .pipe(map(data => Preferences.deserialize(data)));
  }

  public isPrivate() {
    return this.userIdentity && this.userIdentity.account !== null && this.userIdentity.account.type === AccountType.Private;
  }

  get headerSubscription() {
    return this.headearSubscription$.asObservable();
  }

  loadHeaderSubscription(subscription: Subscription[]) {
    this.headearSubscription$.next(subscription);
  }
}
